import React, {
    createContext, useContext, useState, useCallback,
    useMemo,
} from 'react';
import {
    Snackbar,
    Alert,
    AlertColor,
    SnackbarOrigin,
} from '@mui/material';

interface ToastContextType {
    showToast: (message: string, severity?: AlertColor, anchorOrigin?: SnackbarOrigin) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const useToast = (): ToastContextType => {
    const context = useContext(ToastContext);

    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};

interface ToastProviderProps {
    children: React.ReactNode;
}

export const ToastProvider: React.FC<ToastProviderProps> = ({ children }) => {
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState('');
    const [severity, setSeverity] = useState<AlertColor>('info');
    const [anchorOrigin, setAnchorOrigin] = useState<SnackbarOrigin>({ vertical: 'top', horizontal: 'center' });

    const showToast = useCallback((
        msg: string,
        sev?: AlertColor,
        anchor?: SnackbarOrigin,
    ) => {
        setMessage(msg);
        setSeverity(sev ?? 'info');
        setAnchorOrigin(anchor ?? { vertical: 'top', horizontal: 'center' });
        setOpen(true);
    }, []);

    const handleClose = (_event?: React.SyntheticEvent | Event, reason?: string): void => {
        if (reason === 'clickaway') {
            return;
        }
        setOpen(false);
    };

    return (
        <ToastContext.Provider value={useMemo(() => ({ showToast }), [showToast])}>
            {children}
            <Snackbar
                anchorOrigin={anchorOrigin}
                autoHideDuration={6000}
                open={open}
                onClose={handleClose}
            >
                <Alert
                    severity={severity}
                    sx={{ width: '100%' }}
                    variant="filled"
                    onClose={handleClose}
                >
                    {message}
                </Alert>
            </Snackbar>
        </ToastContext.Provider>
    );
};
